import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { resolvePlan } from '@/config/plans';
import { SettingsContext, type SettingsContextValue } from '@/contexts/SettingsContext';
import { useDataStore } from '@/hooks/useDataStore';
import type { Settings } from '@/types';
import { BootSplash } from '@/components/layout/BootSplash';

export function SettingsProvider({ children }: PropsWithChildren) {
  const store = useDataStore();
  const [settings, setSettings] = useState<Settings | null>(null);

  useEffect(() => {
    let cancelled = false;
    void store.settings.read().then((loaded) => {
      if (!cancelled) setSettings(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [store]);

  const update = useCallback(
    async (patch: Partial<Settings>) => {
      const next = await store.settings.patch(patch);
      setSettings(next);
    },
    [store],
  );

  const value = useMemo<SettingsContextValue | null>(() => {
    if (!settings) return null;
    const plan = resolvePlan(settings.plan);
    return { settings, plan, isPro: settings.plan === 'pro', update };
  }, [settings, update]);

  if (!value) return <BootSplash />;

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}
